import { useState } from 'react';
import classNames from 'classnames';

import Nav from 'components/Nav';
import Typography from 'components/Typography';
import Content from 'components/Content';
import Spacer from 'components/Spacer';

import Footer from 'components/Footer';

import classes from 'styles/components/Calculator.module.scss';

import FamilyAndPersonal from 'icons/services/family_and_personal.svg';
import HomeAndRealEstate from 'icons/services/home_and_real_estate.svg';
import MoneyMatters from 'icons/services/money_matters.svg';
import EstatePlanning from 'icons/services/estate_planning.svg';
import ElderCare from 'icons/services/elder_care.svg';
import CivilLawsuits from 'icons/services/civil_lawsuits.svg';
import AutoLaw from 'icons/services/auto_law.svg';
import CheckMark from 'icons/check_mark.svg';
import Button from 'components/Button';

import { REGISTER } from 'utils/constants/urls';
import LAWYER_HOURLY_RATE from 'utils/constants/attorneys';

const navLinks = [{ text: 'About Us', href: '/about-us' }];

const MLP_YEARLY_COST = 240;

const LEGAL_SERVICES = [
  {
    name: 'Family & Personal',
    icon: FamilyAndPersonal,
    services: [
      ['Adoption', 15],
      ['Affidavits', 1],
      ['Conservatorship', 15],
      ['Demand Letters', 1],
      ['Document Review', 1],
      ['Guardianship', 15],
      ['Immigration Assistance', 6],
      ['Juvenile Defense', 15],
      ['Administrative Hearings', 10],
      ['Garnishment Negotiations', 5],
      ['Name Change', 10],
      ['Parental Responsibility Matters', 12],
      ['Prenuptial Agreements', 12],
      ['Protection from Domestic Violence', 12],
      ['School Hearings', 5],
    ],
  },
  {
    name: 'Home & Real Estate',
    icon: HomeAndRealEstate,
    services: [
      ['Deeds', 2],
      ['Eviction and Tenant Problems', 6],
      ['Purchase of a Primary or Vacation Home', 8],
      ['Refinancing and Home Equity Loan', 5],
      ['Tenant Negotiations', 4],
      ['Boundary or Title Disputes', 25],
      ['Foreclosure', 15],
      ['Mortgages', 2],
      ['Personal Property Issues', 5],
      ['Property Tax Assessments', 5],
      ['Sale of a Primary or Vacation Home', 8],
      ['Security Deposit Assistance', 2],
      ['Zoning Applications', 8],
    ],
  },
  {
    name: 'Estate Planning Documents',
    icon: EstatePlanning,
    services: [
      ['Wills', 2],
      ['Codicils', 2],
      ['Living Wills', 1],
      ['Powers of Attorney', 1],
      ['Trusts', 5],
      ['Healthcare Proxies', 1],
    ],
  },
  {
    name: 'Money Matters',
    icon: MoneyMatters,
    services: [
      ['Debt Collection Defense', 10],
      ['Identity Theft', 5],
      ['Personal Bankruptcy', 25],
      ['Creditor Negotiations', 5],
      ['Promissory Notes', 1],
      ['Tax Audit Representation', 10],
      ['Tax Collection Defense', 10],
    ],
  },
  {
    name: 'Auto Law',
    icon: AutoLaw,
    services: [
      ['Drivers License Restoration', 5],
      ['Traffic Ticket Defense', 3],
      ['Repossession', 5],
    ],
  },
  {
    name: 'Elder Care',
    icon: ElderCare,
    services: [
      ['Document Review', 2],
      ['Elder Law Matters', 1],
    ],
  },
  {
    name: 'Civil Lawsuits',
    icon: CivilLawsuits,
    services: [
      ['Civil Litigation Defense', 25],
      ['Small Claims Assistance', 2],
      ['Disputes over consumer goods and services', 15],
      ['Incompetency Defense', 10],
      ['Pet Liabilities', 5],
    ],
  },
];

const Services = () => {
  const [serviceCategoryIndex, setServiceCategoryIndex] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [nationalCost, setNationalCost] = useState(0);

  const serviceCategory = LEGAL_SERVICES[serviceCategoryIndex];
  const serviceNamesList = serviceCategory.services.flat();
  const metlifeCost = selectedServices.length ? MLP_YEARLY_COST : 0;

  const toggleService = (service, hours) => {
    const serviceIndex = selectedServices.indexOf(service);
    const categoryIndex = selectedCategories.indexOf(serviceCategory.name);
    const currentServices = selectedServices;
    const currentCategories = selectedCategories;
    let newCost = nationalCost;

    // Calculate national average cost each time a service is selected or unselected.
    if (serviceIndex !== -1) {
      currentServices.splice(serviceIndex, 1);
      newCost -= LAWYER_HOURLY_RATE * hours;
    } else {
      currentServices.push(service);
      newCost += LAWYER_HOURLY_RATE * hours;
    }

    // Also update the list of service categories that have services selected.
    if (serviceNamesList.some(name => currentServices.includes(name))) {
      if (categoryIndex === -1) {
        currentCategories.push(serviceCategory.name);
      }
    } else if (categoryIndex !== -1) {
      currentCategories.splice(categoryIndex, 1);
    }

    setSelectedServices([...currentServices]);
    setSelectedCategories([...currentCategories]);
    setNationalCost(newCost);
  };

  return (
    <>
      <Nav links={navLinks} />
      <main role="main">
        <Content className={classes.header}>
          <Spacer v={100} />

          <Typography variant="h1">MetLife Legal Needs Calculator</Typography>

          <Spacer v={32} />

          <Typography variant="subheader1">
            See how cost-effective the Legal Plan is compared to paying for an attorney
            out-of-pocket.
          </Typography>

          <Spacer v={60} />
        </Content>

        <Content className={classes.content}>
          <div className={classes.serviceSection}>
            <div className={classes.wrapper}>
              <Typography variant="h2" unsetColor>
                Legal Services
              </Typography>

              <Spacer v={32} />

              <div className={classes.serviceCategory}>
                {LEGAL_SERVICES.map((category, i) => {
                  const { name, icon: IconComponent } = category;
                  const categorySelected = name === serviceCategory.name;
                  const checked = selectedCategories.includes(name);

                  return (
                    <>
                      <div
                        className={
                          categorySelected ? classNames(classes.box, classes.selected) : classes.box
                        }
                        onClick={() => {
                          setServiceCategoryIndex(i);
                        }}
                      >
                        <IconComponent className={checked ? classes.fade : ''} />
                        <Typography className={categorySelected ? classes.highlighted : ''}>
                          {name}
                        </Typography>

                        {checked && <CheckMark className={classes.checkMark} />}
                      </div>

                      <Spacer h={8} />
                    </>
                  );
                })}
              </div>

              <Spacer v={48} />

              <Typography variant="h2">
                What are your{' '}
                <span className={classes.highlighted}>{serviceCategory.name.toLowerCase()}</span>{' '}
                needs?
              </Typography>

              <Spacer v={48} />

              <div className={classes.servicesList}>
                {serviceCategory.services.map(([serviceName, hours]) => {
                  const isSelected = selectedServices.includes(serviceName);

                  return (
                    <div key={serviceName} className={classes.service}>
                      <div
                        className={classNames(classes.serviceName, {
                          [classes.selected]: isSelected,
                        })}
                      >
                        {isSelected && <CheckMark />}
                        <Typography
                          variant="h4"
                          unsetColor
                          onClick={() => toggleService(serviceName, hours)}
                        >
                          {serviceName}
                        </Typography>
                      </div>

                      <Spacer v={32} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className={classes.costSection}>
            <div className={classes.cost}>
              <div className={classNames(classes.row, classes.national)}>
                <Typography variant="h1" unsetColor>
                  ${nationalCost.toLocaleString()}
                  <sup>PER YEAR</sup>
                </Typography>
                <Typography bold variant="body2" unsetColor className={classes.label}>
                  National Average
                </Typography>
              </div>

              <div className={classNames(classes.row, classes.metlife)}>
                <Typography variant="h1" unsetColor>
                  ${metlifeCost.toLocaleString()}
                  <sup>PER YEAR</sup>
                </Typography>
                <Typography bold variant="body2" unsetColor className={classes.label}>
                  MetLife Plan
                </Typography>
              </div>

              <hr />
              <Spacer v={24} />

              <div className={classNames(classes.row, classes.savings)}>
                <Typography variant="h1" unsetColor>
                  ${(nationalCost - metlifeCost).toLocaleString()}
                  <sup>PER YEAR</sup>
                </Typography>
                <Typography bold variant="body2" unsetColor className={classes.label}>
                  Your Savings
                </Typography>
              </div>
            </div>

            <div className={classes.createAccountSection}>
              <Spacer h={24} />
              <Typography variant="subheader1">Start saving now!</Typography>
              <Spacer h={32} />

              <Button
                filled
                noMinWidth
                color="green"
                onClick={() => {
                  window.location = REGISTER;
                }}
              >
                Create Account
              </Button>
            </div>

            <Spacer v={24} />

            <Typography variant="body2" className={classes.disclaimer}>
              Average hourly rate of ${LAWYER_HOURLY_RATE} per hour based on years of legal
              experience, National Law Journal and ALM Legal Intelligence, Survey of Law Firm
              Economics (2021). Price per year for MetLife Legal Plans based on a rate of $20 per
              month. <b>Your actual cost may vary.</b> All services presented in this tool may not
              be covered by all plans.
            </Typography>
          </div>
        </Content>

        <Spacer v={100} />
      </main>

      <Footer />
    </>
  );
};

export default Services;
